import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { siteImages } from '../data/mock';
import { Zap, Target, Users, Award, Shield, FlaskConical, Truck, ArrowRight, Bolt } from 'lucide-react';

const AboutPage = () => {
  useEffect(() => {
    document.title = 'About Axon Muscle | Premium Supplements';
  }, []);

  return (
    <div className="min-h-screen bg-neutral-950">
      <Header />
      <CartDrawer />
      
      <main className="pt-[104px]">
        {/* Banner */}
        <section className="relative h-72 lg:h-96 overflow-hidden">
          <img 
            src={siteImages.aboutBanner}
            alt="About us"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-950 via-neutral-950/90 to-neutral-950/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-transparent to-transparent" />
          
          {/* Cyan Glow */}
          <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-axon-600/15 rounded-full blur-[120px]" />
          
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-axon-600/20 border border-axon-500/30 rounded-full text-axon-400 text-sm font-semibold mb-6">
                  <Bolt className="w-4 h-4" />
                  OUR STORY
                </div>
                <h1 className="font-display text-4xl lg:text-5xl text-white uppercase tracking-tight mb-4">
                  About Axon Muscle
                </h1>
                <p className="text-neutral-300 text-lg">
                  Premium supplements for athletes who demand more.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20 bg-neutral-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="inline-block px-4 py-1.5 bg-axon-600/20 text-axon-500 text-xs font-bold uppercase tracking-wider rounded-full mb-6">
                  Our Mission
                </span>
                <h2 className="font-display text-3xl lg:text-4xl text-white uppercase tracking-tight mb-6">
                  Power Your Performance
                </h2>
                <p className="text-neutral-400 leading-relaxed mb-6">
                  At Axon Muscle, we believe every athlete deserves access to premium, science-backed supplements. Our mission is to provide the highest quality formulas that support your fitness journey, whether you're a competitive athlete or just starting out.
                </p>
                <p className="text-neutral-400 leading-relaxed">
                  We're committed to transparency, quality, and results. Every product is carefully formulated using research-backed ingredients and manufactured in GMP-certified facilities to ensure you get the best possible supplements for your goals.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-6">
                {[
                  { value: '100%', label: 'Lab Tested' },
                  { value: 'USA', label: 'Manufactured' },
                  { value: '30-Day', label: 'Guarantee' },
                  { value: 'GMP', label: 'Certified' },
                ].map((stat, i) => (
                  <div key={i} className="bg-neutral-900 rounded-2xl border border-neutral-800 p-6 text-center">
                    <p className="font-display text-3xl text-axon-500 font-bold mb-2">{stat.value}</p>
                    <p className="text-neutral-400 text-sm uppercase tracking-wider">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-20 bg-neutral-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="inline-block px-4 py-1.5 bg-electric-500/20 text-electric-400 text-xs font-bold uppercase tracking-wider rounded-full mb-4">
                Core Values
              </span>
              <h2 className="font-display text-3xl lg:text-4xl text-white uppercase tracking-tight">
                What We Stand For
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: FlaskConical,
                  title: 'Quality First',
                  desc: 'Every batch is third-party tested for purity and potency. We never compromise on quality.'
                },
                {
                  icon: Users,
                  title: 'Customer Focus',
                  desc: 'Your goals are our priority. We\'re here to support your fitness journey every step of the way.'
                },
                {
                  icon: Target,
                  title: 'Science-Backed',
                  desc: 'Our formulas are based on the latest research to deliver real, measurable results.'
                },
              ].map((value, i) => (
                <div key={i} className="bg-neutral-950 rounded-2xl border border-neutral-800 p-8 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-axon-600 to-axon-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <value.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-display font-bold text-white text-xl uppercase tracking-wide mb-4">{value.title}</h3>
                  <p className="text-neutral-400 leading-relaxed">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-neutral-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="inline-block px-4 py-1.5 bg-axon-600/20 text-axon-500 text-xs font-bold uppercase tracking-wider rounded-full mb-4">
                Why Us
              </span>
              <h2 className="font-display text-3xl lg:text-4xl text-white uppercase tracking-tight">
                Why Choose Axon Muscle?
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Shield, title: 'Premium Quality', desc: 'Only the highest quality ingredients' },
                { icon: FlaskConical, title: 'Lab Tested', desc: 'Third-party verified for purity' },
                { icon: Truck, title: 'Fast Shipping', desc: 'Free shipping on orders $99+' },
                { icon: Award, title: 'Satisfaction', desc: '30-day money back guarantee' },
              ].map((item, i) => (
                <div key={i} className="bg-neutral-900 rounded-2xl border border-neutral-800 p-6 hover:border-axon-500/50 transition-colors">
                  <div className="w-12 h-12 bg-axon-600/20 rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-axon-500" />
                  </div>
                  <h3 className="font-display font-bold text-white uppercase tracking-wide mb-2">{item.title}</h3>
                  <p className="text-neutral-400 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-neutral-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display text-3xl lg:text-4xl text-white uppercase tracking-tight mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-neutral-400 mb-10 max-w-2xl mx-auto">
              Browse our collection of premium supplements and start your journey to better performance today.
            </p>
            <Link 
              to="/shop"
              className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-axon-600 to-axon-500 hover:from-axon-500 hover:to-axon-400 text-white font-bold uppercase tracking-wider rounded-xl transition-all shadow-button hover:shadow-glow-cyan"
            >
              Shop Now
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default AboutPage;
